import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ADDONS, computeEstimate, api } from "@/lib/api";

const empty = { name: "", email: "", phone: "", address: "", preferred_date: "", notes: "" };

export default function BookingForm({ sqft, setSqft, addons, setAddons }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const estimate = computeEstimate(sqft, addons);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !sqft) {
      toast.error("Please fill in your details and square footage.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bookings", {
        ...form,
        square_footage: Number(sqft),
        addons,
      });
      setDone(true);
      toast.success("Booking request sent! We'll be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="py-24 md:py-32 bg-[#F6F6F2]" data-testid="booking">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="font-mono2 text-xs tracking-[0.25em] text-[#005CEE] mb-4">04 / BOOK YOUR JOB</p>
          <h2 className="font-head font-extrabold tracking-tighter text-5xl md:text-6xl leading-none">
            Lock in your crew.
          </h2>
          <p className="mt-6 text-[#8A8A85] leading-relaxed max-w-md">
            Send us the details and your instant estimate travels with the request. We confirm timing and finalise the quote on a free site visit.
          </p>
          <div className="mt-10 bg-[#111] text-white rounded-3xl p-8">
            <p className="text-xs font-mono2 tracking-widest text-[#94A3B8]">YOUR ESTIMATE</p>
            <p className="font-mono2 text-5xl mt-2">${estimate.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-xs text-[#94A3B8] mt-2 font-mono2">{Number(sqft) || 0} sq ft · {ADDONS.filter((a) => addons[a.key]).map((a) => a.label).join(", ") || "no add-ons"}</p>
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7 bg-white rounded-3xl border border-[#E5E5DF] p-8 shadow-xl"
          data-testid="booking-form"
        >
          {done ? (
            <div className="py-16 text-center" data-testid="booking-success">
              <p className="font-head font-extrabold text-4xl tracking-tighter">Thank you!</p>
              <p className="mt-4 text-[#8A8A85]">Your request is in. We'll reach out to confirm your visit.</p>
              <button type="button" onClick={() => setDone(false)} className="btn-dark mt-8 px-6 py-3 text-sm">Send another</button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" testid="book-name"><input value={form.name} onChange={set("name")} className="odm-input" data-testid="book-name" /></Field>
              <Field label="Phone" testid="book-phone"><input value={form.phone} onChange={set("phone")} className="odm-input" data-testid="book-phone" /></Field>
              <Field label="Email" testid="book-email"><input type="email" value={form.email} onChange={set("email")} className="odm-input" data-testid="book-email" /></Field>
              <Field label="Preferred date"><input type="date" value={form.preferred_date} onChange={set("preferred_date")} className="odm-input" data-testid="book-date" /></Field>
              <div className="sm:col-span-2">
                <Field label="Property address"><input value={form.address} onChange={set("address")} className="odm-input" data-testid="book-address" /></Field>
              </div>
              <div className="sm:col-span-2 grid grid-cols-2 gap-5">
                <Field label="Square footage"><input type="number" min="0" value={sqft} onChange={(e) => setSqft(e.target.value)} className="odm-input font-mono2" data-testid="book-sqft" /></Field>
                <div>
                  <label className="odm-label">Add-ons</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {ADDONS.map((a) => (
                      <button type="button" key={a.key} onClick={() => setAddons({ ...addons, [a.key]: !addons[a.key] })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${addons[a.key] ? "bg-[#005CEE] text-white border-[#005CEE]" : "border-[#E5E5DF] text-[#8A8A85]"}`}
                        data-testid={`book-addon-${a.key}`}>
                        {a.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Field label="Notes (optional)"><textarea rows="3" value={form.notes} onChange={set("notes")} className="odm-input resize-none" data-testid="book-notes" /></Field>
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-sm font-semibold disabled:opacity-60" data-testid="book-submit">
                  {loading ? "Sending…" : "Send Booking Request →"}
                </button>
              </div>
            </div>
          )}
        </motion.form>
      </div>

      <style>{`
        .odm-label { display:block; font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:.15em; text-transform:uppercase; color:#8A8A85; }
        .odm-input { width:100%; background:#F6F6F2; border:1px solid #E5E5DF; border-radius:12px; padding:12px 14px; font-size:14px; outline:none; transition:border-color .2s; margin-top:8px; }
        .odm-input:focus { border-color:#005CEE; }
      `}</style>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="odm-label">{label}</span>
      {children}
    </label>
  );
}
